import PhotoSwipeLightbox from 'photoswipe5/lightbox';
import getDOMfromString from '../../helpers/getDOMfromString';
import tabTrap from '../../helpers/tabTrap';
// import triggerViewRecord from '../../helpers/triggerViewRecord';
import {gsap} from '../../vendors/gsap';
import {ScrollTrigger} from '../../vendors/gsap/ScrollTrigger';
import appData from '../core/appData';

import 'photoswipe5/dist/photoswipe.css';
import './zoom.css';

/**
 * This module contains data and functions to handle zoom view
 *
 * @param {Object} config - passed params which can be properties/methods
 * @returns {Object} - properties and methods
 */
const zoomView = () => {
    // const {resetTriggerPageInfo} = config;
    const outsideElements =
        '.story-container a,.story-container button,.header a,.header button,.app-menu a,.app-menu button';

    // remove zoom view if already present
    const zoomViewContainer = document.querySelector('.zoom-view');
    if (zoomViewContainer) {
        zoomViewContainer.remove();
    }

    let activeIndex = 0;
    let activeItemId = null;

    // creating the zoom container
    const container = document.querySelector('.app-container');
    const templateZoomView = document.querySelector('#app-zoom');
    const html = templateZoomView.content.cloneNode(true);
    const appZoom = html.querySelector('.app-zoom');
    appZoom.classList.add('zoom-view');
    container.append(html);

    const zoomCloseButton = document.querySelector('[data-action=zoom-button-close]');

    const zoomContainer = document.querySelector('.zoom-container');
    const closeSVGString =
        '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="none" viewBox="0 0 24 24" aria-hidden="true" class="pswp__icn" width="24" height="24"><mask id="a" width="24" height="24" x="0" y="0" maskUnits="userSpaceOnUse" style="mask-type:alpha"><path fill="white" d="M0 0h24v24H0z"/></mask><g mask="url(#a)"><path fill="white" fill-rule="evenodd" d="m13.063 12.002 2.94-2.94L14.943 8l-2.941 2.94-2.94-2.94L8 9.061l2.94 2.941L8 14.943l1.06 1.061 2.942-2.941 2.941 2.941 1.061-1.06-2.941-2.942Z" clip-rule="evenodd"/></g></svg>';

    /**
     * Create elements to be shown on the page
     */
    const loadContent = () => {
        const zoomWrapper = document.createElement('div');
        zoomWrapper.classList.add('zoom-wrapper');

        // clearing zoom container
        zoomContainer.replaceChildren();

        zoomContainer.append(zoomWrapper);

        appData.getZoomStoryData().forEach(item => {
            const htmlStr = `
                <figure
                    class="zoom-card"
                    data-item-id="${item.id}"
                    data-portrait="${item.portrait}"
                    data-ratio="${item.aspectRatio}"
                >
                    <div class="zoom-card-image">
                        <img src="${item.src}" alt="${item.label}" class="zoom-image lazy" data-src="${item.dataSrc}" />
                    </div>
                    <figcaption class="zoom-card-caption">
                        <a href="#" class="zoom-title-container" data-item-id="${item.id}">
                            <div class="pointer-events-none">
                                <p>${item.title}</p>
                                <p>${item.description ?? ''}</p>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 9 9" width="9" height="9">
                                <path fill="#fff" d="M1.563 8.5.5 7.437 6.438 1.5H1V0h8v8H7.5V2.562L1.562 8.5Z" />
                            </svg>
                        </a>
                    </figcaption>
                </figure>
            `;
            const cardContainer = getDOMfromString(htmlStr);
            zoomWrapper.append(cardContainer);
        });

        const lightbox = new PhotoSwipeLightbox({
            dataSource: appData.getZoomStoryData().map(item => ({
                src: item.zoomedSrc ?? item.src,
                width: item.width,
                height: item.height,
                alt: item.alt
            })),
            initialZoomLevel: 'fill',
            secondaryZoomLevel: 'fill',
            allowPanToNext: false,
            arrowKeys: false,
            closeSVG: closeSVGString,
            preloaderDelay: 0,
            // eslint-disable-next-line jsdoc/require-jsdoc
            pswpModule: () => import('photoswipe5')
        });

        lightbox.init();

        document.querySelector('button.zoom-button-more').addEventListener('click', () => {
            const zoomCardCaptions = Array.from(document.querySelectorAll('.zoom-card-caption'));
            activeIndex = Number(zoomCardCaptions.findIndex(item => item.style.opacity === '1') ?? 0);

            lightbox.loadAndOpen(activeIndex);
        });
    };

    /**
     * Handles the click event for the open zoom button.
     *
     * @param {number} slideIndex - active slide index
     */
    const onZoomOpenClick = slideIndex => {
        activeIndex = slideIndex;
        const zoomOpenButton = document.querySelector('.zoom-btn');
        zoomOpenButton.classList.toggle('active');

        const currentAriaExpanded = zoomOpenButton.getAttribute('aria-expanded');
        const newAriaExpanded = currentAriaExpanded === 'true' ? 'false' : 'true';
        zoomOpenButton.setAttribute('aria-expanded', newAriaExpanded);
        tabTrap.enable(outsideElements);

        // scroll to the provide slide index
        if (Number.isInteger(slideIndex)) {
            const zoomItems = document.querySelector('.zoom-wrapper').children;
            zoomItems.item(slideIndex).scrollIntoView();
        }
    };

    /**
     * Handles the click event for the close zoom button.
     *
     * @param {Function} onClose - callback function
     */
    const onZoomCloseClick = onClose => {
        const zoomOpenButton = document.querySelector('.zoom-btn');
        const zoomCardCaptions = Array.from(document.querySelectorAll('.zoom-card-caption'));
        activeIndex = Number(zoomCardCaptions.findIndex(item => item.style.opacity === '1') ?? 0);
        if (onClose) {
            onClose(activeIndex);
        }
        zoomOpenButton.classList.remove('active');
        zoomOpenButton.setAttribute('aria-expanded', 'false');
        tabTrap.disable(outsideElements);
    };

    /**
     * Sets the click event for the close zoom button.
     *
     * @param {Function} onClose - callback function
     */
    const onZoomCloseClickEvent = onClose => {
        zoomCloseButton.addEventListener('click', () => {
            onZoomCloseClick(onClose);
        });
    };

    /**
     * Handles the click event for the zoom title.
     *
     * @param {Event} e - click event
     * @param {number} index - slide index
     */
    // const onZoomTitleClick = (e, index) => {
    //     e.preventDefault();
    //     resetTriggerPageInfo();
    //     triggerViewRecord(e, index);
    //     zoomCloseButton.click();
    // };

    /**
     * Handles the scoll end event
     */
    const onScrollEnd = () => {
        // Update the active story data
        if (activeItemId !== null) {
            appData.setActiveStory(activeItemId);
        }
    };

    /**
     * Initial data setup and event listeners
     */
    const init = () => {
        gsap.set('.app-zoom', {autoAlpha: 0});

        loadContent();

        gsap.registerPlugin(ScrollTrigger);

        // Remove and set in case this gets called twice
        ScrollTrigger.removeEventListener('scrollEnd', onScrollEnd);
        ScrollTrigger.addEventListener('scrollEnd', onScrollEnd);

        gsap.utils.toArray('.zoom-card').forEach(card => {
            const cardImg = card.querySelector('.zoom-image');
            const cardCaption = card.querySelector('.zoom-card-caption');

            gsap.timeline({
                scrollTrigger: {
                    scroller: card.closest('.zoom-wrapper'),
                    trigger: card,
                    start: 'center bottom',
                    end: 'center center',
                    scrub: true,
                    /**
                     * On enter, update the active item id
                     */
                    onEnter: () => {
                        activeItemId = card.dataset.itemId;
                    }
                }
            })
                .from(cardImg, {opacity: 0.2})
                .from(cardCaption, {opacity: 0});

            gsap.timeline({
                scrollTrigger: {
                    scroller: card.closest('.zoom-wrapper'),
                    trigger: card,
                    start: 'center center',
                    end: 'bottom top',
                    scrub: true,
                    /**
                     * On enter, update the active item id
                     */
                    onEnter: () => {
                        activeItemId = card.dataset.itemId;
                    }
                }
            })
                .to(cardImg, {opacity: 0.2})
                .to(cardCaption, {opacity: 0});
        });

        const landscapeImages = document.querySelectorAll('.zoom-card');

        const landscapeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const {width: contentWidth, height: contentHeight} = entry.contentRect;
                const imageRatio = entry.target.dataset.ratio;
                const imageNaturalHeight = contentWidth / imageRatio;
                const captionHeight = entry.target.querySelector('.zoom-card-caption').getBoundingClientRect().height;
                if (imageNaturalHeight + captionHeight > contentHeight) {
                    entry.target.classList.add('fit');
                } else {
                    entry.target.classList.remove('fit');
                }
            }
        });
        landscapeImages.forEach(landscapeImage => {
            landscapeObserver.observe(landscapeImage);
        });

        // zoom view title click event handlers
        // const zoomTitles = document.querySelectorAll('.zoom-title-container');
        // zoomTitles.forEach((item, index) => item.addEventListener('click', e => onZoomTitleClick(e, index)));
    };

    init();

    return {onZoomOpenClick, onZoomCloseClick, onZoomCloseClickEvent};
};

export default zoomView;
